import type { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DateTimePicker } from '@nucleus-commerce/admin-kit/date-time';
import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormLayout, FormSection, MoneyInput, TagInput } from '@nucleus-commerce/admin-kit/forms';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import {
  DiscountClass,
  DiscountKind,
  DiscountMethod,
  type DiscountStatus,
} from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { CountrySelect } from '@/features/markets';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import {
  DISCOUNT_SCOPES,
  discountFormSchema,
  type DiscountFormInput,
  type DiscountFormOutput,
} from '../schemas/discount-form-schema';
import { discountErrorTarget, discountFieldPath } from '../schemas/discount-rules';

import { AppliesToCollectionsField } from './applies-to-collections-field';
import { AppliesToProductsField } from './applies-to-products-field';

export const DISCOUNT_FORM_ID = 'discount-form';

const KNOWN_FIELDS: ReadonlySet<string> = new Set([
  'title',
  'method',
  'percentage',
  'amount',
  'productIds',
  'collectionIds',
  'minSubtotal',
  'minQuantity',
  'firstOrderOnly',
  'countries',
  'combinesWith',
  'usageLimitTotal',
  'usageLimitPerCustomer',
  'startsAt',
  'endsAt',
  'status',
  'codes',
]);

const STATUS_ORDER: DiscountStatus[] = ['active', 'disabled'];

export interface DiscountFormProps {
  storeId: string;
  currency: string;
  mode: 'create' | 'edit';
  defaultValues: DiscountFormInput;
  isSubmitting: boolean;
  onSubmit: (values: DiscountFormOutput) => Promise<unknown>;
  /** Sections under the form in the main column: codes and usages on edit. */
  children?: ReactNode;
  dangerZone?: ReactNode;
}

function Choice({
  id,
  value,
  current,
  title,
  hint,
  disabled,
}: {
  id: string;
  value: string;
  current: string;
  title: string;
  hint: string;
  disabled?: boolean;
}) {
  return (
    <label
      htmlFor={id}
      className={cn(
        'flex items-start gap-3 rounded-md border p-3',
        disabled ? 'opacity-60' : 'cursor-pointer',
        current === value && 'border-primary bg-primary-tint/40',
      )}
    >
      <RadioGroupItem id={id} value={value} className="mt-0.5" disabled={disabled} />
      <span className="grid gap-0.5 text-sm">
        <span className="font-medium">{title}</span>
        <span className="text-muted-foreground text-xs">{hint}</span>
      </span>
    </label>
  );
}

/**
 * Method, scope, conditions, combinations, limits and codes inside the <form>; status and the
 * save button in the side rail bound to the same react-hook-form context.
 */
export function DiscountForm({
  storeId,
  currency,
  mode,
  defaultValues,
  isSubmitting,
  onSubmit,
  children,
  dangerZone,
}: DiscountFormProps) {
  const { t } = useTranslation(['discounts', 'common']);
  const message = useErrorMessage();
  const form = useForm<DiscountFormInput, unknown, DiscountFormOutput>({
    resolver: useI18nResolver(discountFormSchema(mode)),
    defaultValues,
  });
  const kind = form.watch('kind');
  const method = form.watch('method');
  const scope = form.watch('scope');
  const isDirty = form.formState.isDirty;
  const rootError = form.formState.errors.root?.server?.message;

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await onSubmit(values);
      if (mode === 'edit') form.reset(values);
    } catch (error) {
      if (
        applyFieldErrors(error, form.setError, {
          knownFields: KNOWN_FIELDS,
          mapField: discountFieldPath,
        })
      ) {
        return;
      }
      const target = discountErrorTarget(error);
      form.setError(target?.field ?? 'root.server', {
        type: 'server',
        message: target ? t(target.key, target.values) : message(error),
      });
    }
  });
  useSaveBar({ form, onSave: submit, saving: isSubmitting });

  const whole = (
    name: 'minQuantity' | 'usageLimitTotal' | 'usageLimitPerCustomer',
    hint?: string,
  ) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{t(`discounts:fields.${name}`)}</FormLabel>
          <FormControl>
            <Input inputMode="numeric" autoComplete="off" {...field} />
          </FormControl>
          {hint ? <FormDescription>{hint}</FormDescription> : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );

  const moment = (name: 'startsAt' | 'endsAt') => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{t(`discounts:fields.${name}`)}</FormLabel>
          <FormControl>
            <DateTimePicker {...field} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  return (
    <Form {...form}>
      <FormLayout
        aside={
          <>
            <FormSection title={t('discounts:sections.status')}>
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <RadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                        className="gap-2.5"
                        aria-label={t('discounts:sections.status')}
                      >
                        {STATUS_ORDER.map((status) => (
                          <div key={status} className="flex items-start gap-2.5">
                            <RadioGroupItem
                              value={status}
                              id={`discount-status-${status}`}
                              className="mt-0.5"
                            />
                            <div className="grid gap-0.5">
                              <Label htmlFor={`discount-status-${status}`}>
                                {t(`discounts:status.${status}`)}
                              </Label>
                              <span className="text-muted-foreground text-xs">
                                {t(`discounts:statusHint.${status}`)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Can permission="discounts.write">
                <div className="flex items-center gap-2 border-t pt-4">
                  <Button
                    type="submit"
                    form={DISCOUNT_FORM_ID}
                    data-tour="discounts.editor.save"
                    disabled={isSubmitting || (mode === 'edit' && !isDirty)}
                    className="flex-1"
                  >
                    {isSubmitting
                      ? t('common:actions.saving')
                      : mode === 'create'
                        ? t('discounts:create.submit')
                        : t('common:actions.save')}
                  </Button>
                  {mode === 'edit' ? (
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={isSubmitting || !isDirty}
                      onClick={() => form.reset()}
                    >
                      {t('discounts:actions.discard')}
                    </Button>
                  ) : null}
                </div>
              </Can>
            </FormSection>
            {dangerZone}
          </>
        }
      >
        <form id={DISCOUNT_FORM_ID} onSubmit={submit} noValidate className="flex flex-col gap-6">
          {rootError ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{rootError}</AlertDescription>
            </Alert>
          ) : null}
          <FormSection
            tour="discounts.editor.method"
            title={t('discounts:sections.method')}
            description={t('discounts:sections.methodDescription')}
          >
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('discounts:fields.title')}</FormLabel>
                  <FormControl>
                    <Input autoFocus={mode === 'create'} autoComplete="off" {...field} />
                  </FormControl>
                  <FormDescription>{t('discounts:fields.titleHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="kind"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('discounts:fields.kind')}</FormLabel>
                  <FormControl>
                    <RadioGroup
                      value={field.value}
                      onValueChange={(v) => {
                        field.onChange(v);
                        if (v === 'automatic') form.setValue('codes', []);
                      }}
                      className="grid gap-2 sm:grid-cols-2"
                    >
                      {Object.values(DiscountKind).map((k) => (
                        <Choice
                          key={k}
                          id={`discount-kind-${k}`}
                          value={k}
                          current={field.value}
                          title={t(`discounts:kind.${k}`)}
                          hint={t(`discounts:kindHint.${k}`)}
                          disabled={mode === 'edit'}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                  {mode === 'edit' ? (
                    <FormDescription>{t('discounts:fields.kindLocked')}</FormDescription>
                  ) : null}
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="method"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('discounts:fields.method')}</FormLabel>
                  <FormControl>
                    <RadioGroup
                      value={field.value}
                      onValueChange={field.onChange}
                      className="grid gap-2 sm:grid-cols-3"
                    >
                      {Object.values(DiscountMethod).map((m) => (
                        <Choice
                          key={m}
                          id={`discount-method-${m}`}
                          value={m}
                          current={field.value}
                          title={t(`discounts:method.${m}`)}
                          hint={t(`discounts:methodHint.${m}`)}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {method === 'percentage' ? (
              <FormField
                control={form.control}
                name="percentage"
                render={({ field }) => (
                  <FormItem className="max-w-48">
                    <FormLabel>{t('discounts:fields.percentage')}</FormLabel>
                    <div className="relative">
                      <FormControl>
                        <Input inputMode="decimal" autoComplete="off" className="pr-8" {...field} />
                      </FormControl>
                      <span
                        className="text-muted-foreground pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm"
                        aria-hidden
                      >
                        %
                      </span>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}
            {method === 'fixed_amount' ? (
              <FormField
                control={form.control}
                name="amount"
                render={({ field, fieldState }) => (
                  <FormItem className="max-w-60">
                    <FormLabel>{t('discounts:fields.amount')}</FormLabel>
                    <FormControl>
                      <MoneyInput
                        currency={currency}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        aria-invalid={fieldState.invalid}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}
          </FormSection>

          <FormSection
            tour="discounts.editor.applies-to"
            title={t('discounts:sections.appliesTo')}
            description={t('discounts:sections.appliesToDescription')}
          >
            <FormField
              control={form.control}
              name="scope"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <RadioGroup
                      value={field.value}
                      onValueChange={field.onChange}
                      className="grid gap-2 sm:grid-cols-3"
                      aria-label={t('discounts:sections.appliesTo')}
                    >
                      {DISCOUNT_SCOPES.map((s) => (
                        <Choice
                          key={s}
                          id={`discount-scope-${s}`}
                          value={s}
                          current={field.value}
                          title={t(`discounts:scope.${s}`)}
                          hint={t(`discounts:scopeHint.${s}`)}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </FormItem>
              )}
            />
            {scope === 'products' ? (
              <FormField
                control={form.control}
                name="productIds"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>{t('discounts:fields.productIds')}</FormLabel>
                    <FormControl>
                      <AppliesToProductsField
                        storeId={storeId}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        aria-invalid={fieldState.invalid}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}
            {scope === 'collections' ? (
              <FormField
                control={form.control}
                name="collectionIds"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>{t('discounts:fields.collectionIds')}</FormLabel>
                    <FormControl>
                      <AppliesToCollectionsField
                        storeId={storeId}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        aria-invalid={fieldState.invalid}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}
          </FormSection>

          <FormSection
            tour="discounts.editor.conditions"
            title={t('discounts:sections.conditions')}
            description={t('discounts:sections.conditionsDescription')}
          >
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="minSubtotal"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>{t('discounts:fields.minSubtotal')}</FormLabel>
                    <FormControl>
                      <MoneyInput
                        currency={currency}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        aria-invalid={fieldState.invalid}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {whole('minQuantity')}
            </div>
            <FormField
              control={form.control}
              name="firstOrderOnly"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center gap-2.5">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(v) => field.onChange(v === true)}
                    />
                  </FormControl>
                  <FormLabel className="font-normal">
                    {t('discounts:fields.firstOrderOnly')}
                  </FormLabel>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="countries"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>{t('discounts:fields.countries')}</FormLabel>
                  <FormControl>
                    <CountrySelect
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      aria-invalid={fieldState.invalid}
                    />
                  </FormControl>
                  <FormDescription>{t('discounts:fields.countriesHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>

          <FormSection
            tour="discounts.editor.combinations"
            title={t('discounts:sections.combinations')}
            description={t('discounts:sections.combinationsDescription')}
          >
            <FormField
              control={form.control}
              name="combinesWith"
              render={({ field }) => (
                <FormItem className="gap-3">
                  {Object.values(DiscountClass).map((cls) => {
                    const id = `discount-combines-${cls}`;
                    const checked = field.value.includes(cls);
                    return (
                      <div key={cls} className="flex items-start gap-2.5">
                        <Checkbox
                          id={id}
                          checked={checked}
                          className="mt-0.5"
                          onCheckedChange={(v) =>
                            field.onChange(
                              v === true
                                ? [...field.value, cls]
                                : field.value.filter((c) => c !== cls),
                            )
                          }
                        />
                        <div className="grid gap-0.5">
                          <Label htmlFor={id}>{t(`discounts:combines.${cls}`)}</Label>
                          <span className="text-muted-foreground text-xs">
                            {t(`discounts:combinesHint.${cls}`)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>

          <FormSection
            tour="discounts.editor.limits"
            title={t('discounts:sections.limits')}
            description={t('discounts:sections.limitsDescription')}
          >
            <div className="grid gap-4 md:grid-cols-2">
              {whole('usageLimitTotal', t('discounts:fields.usageLimitTotalHint'))}
              {whole('usageLimitPerCustomer', t('discounts:fields.usageLimitPerCustomerHint'))}
              {moment('startsAt')}
              {moment('endsAt')}
            </div>
            <p className="text-muted-foreground text-xs">{t('discounts:fields.scheduleHint')}</p>
          </FormSection>

          {mode === 'create' && kind === 'code' ? (
            <FormSection
              title={t('discounts:sections.codes')}
              description={t('discounts:sections.codesCreateDescription')}
            >
              <FormField
                control={form.control}
                name="codes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('discounts:fields.codes')}</FormLabel>
                    <FormControl>
                      <TagInput
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        max={100}
                        maxLength={40}
                        placeholder={t('discounts:fields.codesPlaceholder')}
                        removeLabel={(code) => t('discounts:codes.remove', { code })}
                        className="font-mono"
                      />
                    </FormControl>
                    <FormDescription>{t('discounts:fields.codesHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormSection>
          ) : null}
        </form>
        {children}
      </FormLayout>
    </Form>
  );
}
